<template>
  <div id="single-preboot-new-dp-loader" class="dp-loader">
    <canvas id="logoCanvas"></canvas>
  </div>
</template>

<script setup lang="ts">
import { simpleDebugLog } from '../utils/simpleDebugLogger';

// Declare the custom properties on the Window interface
declare global {
  interface Window {
    _animationReqId: number | null;
    _prebootStartTime: number;
    dpLoadLogo?: {
      animate: (timestamp: number) => void;
    };
  }
}

document.addEventListener('DOMContentLoaded', event => {
  window._animationReqId = null;
  window._prebootStartTime = Date.now();
  let dpLoadLogo = (function () {
    const htmlElement = document.documentElement;
    let pausedTime = 0;
    let pausedAtFade = false;
    function getCurrentTheme() {
      return htmlElement.getAttribute('data-dt-theme');
    }

    const observer = new MutationObserver(mutations => {
      for (const mutation of mutations) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-dt-theme') {
          colors = getColors(getCurrentTheme());
          // Update fills on paths:
          letterPaths.forEach(pathObj => {
            pathObj.fill = colors.logo;
          });
          starPath.fill = colors.purple;
          // Redraw current frame immediately with updated colors:
          requestAnimationFrame(animate);
        }
      }
    });
    // Subscribe to changes in the theme to switch colors
    observer.observe(htmlElement, {
      attributes: true,
      attributeFilter: ['data-dt-theme'],
    });

    function getColors(theme: string | null) {
      return theme === 'light'
        ? {
            deepBlue: '#10022C',
            darkPurple: '#1D0155',
            purple: '#7C52FF',
            dynamicPurple: '#7C52FF',
            magenta: '#FF1BA4',
            red: '#FF1356',
            darkGold: '#FF9E0E',
            transparent: '#00000000',
            logo: '#10022C',
            surfacePrimary: '#FFFFFF',
          }
        : {
            deepBlue: '#10022C',
            darkPurple: '#1D0155',
            purple: '#7C52FF',
            dynamicPurple: '#A38FF9',
            magenta: '#FF1BA4',
            red: '#FF1356',
            darkGold: '#FF9E0E',
            transparent: '#00000000',
            logo: '#FFFFFF',
            surfacePrimary: '#1F1F1F',
          };
    }

    let colors = getColors(getCurrentTheme());
    const canvas = document.getElementById('logoCanvas') as HTMLCanvasElement;
    if (!canvas) return; // Exit if canvas element is not found

    const ctx = canvas.getContext('2d');
    if (!ctx) return; // Exit if context cannot be obtained

    let ANIMATION_DURATION = 6;

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    function resizeCanvas() {
      if (!canvas || !ctx) return; // Safety check

      // Pixel ratio for device screen for crisp svgs
      const dpr = window.devicePixelRatio || 1;

      const width = window.innerWidth;
      const height = window.innerHeight;

      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = width + 'px';
      canvas.style.height = height + 'px';

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    const LogoPaths = {
      d: new Path2D(
        'M67.8144 103.499C53.2002 103.499 42.7026 91.7058 42.7026 75.5683C42.7026 59.4308 53.2002 47.7414 67.8144 47.7414C74.6069 47.7414 81.0907 51.1551 83.9724 55.6033V26.8454H95.9108V102.464H83.9724V95.5333C81.0907 99.9815 74.6069 103.499 67.8144 103.499ZM69.7698 93.0506C77.4886 93.0506 83.252 87.0508 83.9724 78.8786V72.0512C83.252 63.879 77.4886 58.1894 69.7698 58.1894C61.1248 58.1894 54.9498 65.1203 54.9498 75.4649C54.9498 85.8094 61.1248 93.0506 69.7698 93.0506Z'
      ),
      i: new Path2D(
        'M115.016 102.464H102.974V48.7759H115.016V102.464ZM108.841 41.4313C104.621 41.4313 101.431 38.121 101.431 33.8797C101.431 29.7419 104.621 26.4316 108.841 26.4316C113.266 26.4316 116.56 29.7419 116.56 33.8797C116.56 38.121 113.266 41.4313 108.841 41.4313Z'
      ),
      a: new Path2D(
        'M120.5 87.8781C120.5 96.8779 128.322 103.498 139.128 103.498C144.891 103.498 150.86 101.326 154.771 95.4296V102.464H166.71V67.7062C166.71 55.1893 157.653 47.7412 144.377 47.7412C131.378 47.7412 123.904 54.9807 121.375 63.051L132.796 64.5546C134.455 60.092 138.222 57.0513 144.377 57.0513C150.346 57.0513 154.771 60.5685 154.771 66.9821V68.8441L139.231 71.1199C127.498 72.775 120.5 78.4646 120.5 87.8781ZM141.186 94.4986C136.04 94.4986 132.747 91.3953 132.747 87.3609C132.747 83.0162 135.732 80.6369 141.186 79.7059L154.771 77.3267V83.8437C152.919 89.4298 148.39 94.4986 141.186 94.4986Z'
      ),
      l: new Path2D('M185.825 102.464H173.783V26.8454H185.825V102.464Z'),
      p: new Path2D(
        'M193.154 121.809V48.7759H205.196V55.7067C207.974 51.2586 214.458 47.7414 221.559 47.7414C235.968 47.7414 246.465 59.5342 246.465 75.6718C246.465 91.8093 235.968 103.499 221.354 103.499C214.458 103.499 207.974 99.9815 205.196 95.5333V121.809H193.154ZM219.398 93.0506C227.94 93.0506 234.218 86.1198 234.218 75.7752C234.218 65.4306 227.94 58.1894 219.604 58.1894C211.576 58.1894 205.916 64.1893 205.196 72.3615V79.1889C205.813 87.3611 211.576 93.0506 219.398 93.0506Z'
      ),
      a2: new Path2D(
        'M250.5 87.8783C250.5 96.8781 258.322 103.499 269.128 103.499C274.891 103.499 280.86 101.326 284.771 95.4299V102.464H296.71V67.7065C296.71 55.1895 287.653 47.7414 274.377 47.7414C261.378 47.7414 253.904 54.9809 251.375 63.0513L262.796 64.5548C264.455 60.0922 268.222 57.0515 274.377 57.0515C280.346 57.0515 284.771 60.5687 284.771 66.9823V68.8443L269.231 71.1201C257.498 72.7753 250.5 78.4648 250.5 87.8783ZM271.186 94.4989C266.04 94.4989 262.747 91.3955 262.747 87.3611C262.747 83.0164 265.732 80.6371 271.186 79.7061L284.771 77.3269V83.844C282.919 89.43 278.39 94.4989 271.186 94.4989Z'
      ),
      d2: new Path2D(
        'M327.758 103.499C313.144 103.499 302.646 91.7058 302.646 75.5683C302.646 59.4308 313.144 47.7414 327.758 47.7414C334.551 47.7414 341.034 51.1551 343.916 55.6033V26.8454H355.854V102.464H343.916V95.5333C341.034 99.9815 334.551 103.499 327.758 103.499ZM329.714 93.0506C337.432 93.0506 343.196 87.0508 343.916 78.8786V72.0512C343.196 63.879 337.432 58.1894 329.714 58.1894C321.068 58.1894 314.893 65.1203 314.893 75.4649C314.893 85.8094 321.068 93.0506 329.714 93.0506Z'
      ),
      star: new Path2D(
        'M54.3367 26.2938C55.1013 26.4648 55.2529 27.7128 54.5379 28.0381C40.7377 32.7358 29.8751 44.7461 24.2032 60.7345C23.8928 61.5097 22.5311 61.3226 22.4031 60.4954C20.4628 47.9557 11.7991 37.9435 0.638063 35.2418C-0.10155 35.0628 -0.241218 33.9009 0.44725 33.572C14.6171 27.7419 25.2666 15.7823 30.1874 0.502581C30.4947 -0.294635 31.9795 -0.0932903 32.1059 0.753666C34.0137 13.5435 42.9098 23.7385 54.3367 26.2938Z'
      ),
    };

    interface LetterPath {
      name: string;
      path: Path2D;
      fill: string;
      x: number;
      y: number;
    }

    interface StarPath {
      name: string;
      path: Path2D;
      fill: string;
      x: number;
      y: number;
      width: number;
      height: number;
    }

    interface Animation {
      rotation: number;
      opacity: number;
      scale: number;
      duration: number;
      delay: number;
    }

    interface StarAnimation {
      scale: number;
      opacity: number;
      duration: number;
      delay: number;
    }

    interface Circle {
      x: number;
      y: number;
      rX: number;
      rY: number;
      color: string;
    }

    const letterPaths: LetterPath[] = [
      { name: 'd', path: LogoPaths.d, fill: colors.logo, x: 69, y: 75 },
      { name: 'i', path: LogoPaths.i, fill: colors.logo, x: 108, y: 75 },
      { name: 'a', path: LogoPaths.a, fill: colors.logo, x: 143, y: 75 },
      { name: 'l', path: LogoPaths.l, fill: colors.logo, x: 179, y: 75 },
      { name: 'p', path: LogoPaths.p, fill: colors.logo, x: 219, y: 75 },
      { name: 'a2', path: LogoPaths.a2, fill: colors.logo, x: 273, y: 75 },
      { name: 'd2', path: LogoPaths.d2, fill: colors.logo, x: 329, y: 75 },
    ];

    const starPath: StarPath = {
      name: 'star',
      path: LogoPaths.star,
      fill: colors.deepBlue,
      x: 0,
      y: 0,
      width: 55,
      height: 61,
    };

    const gradientDefs = {
      x1: 40,
      y1: 45,
      r1: 30,
      x2: 41,
      y2: 41,
      r2: 8,
      stop1: 0,
      stop2: 0.3,
      stop3: 0.6,
      stop4: 0.8,
      stop5: 1,
    };

    // Initial animation setup for each path
    const animations: Animation[] = letterPaths.map((_, i) => ({
      rotation: 0,
      opacity: 0,
      scale: 1.2,
      duration: 0.5,
      delay: ANIMATION_DURATION * 0.5 - 0.5 + i * 0.085,
    }));

    const starAnimation: StarAnimation = {
      scale: 60,
      opacity: 1,
      duration: ANIMATION_DURATION * 0.5,
      delay: 0,
    };

    function ease(easing: string, t: number): number {
      switch (easing) {
        case 'easeOutExpo':
          return t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
        case 'easeInOutExpo':
          return t === 0
            ? 0
            : t === 1
              ? 1
              : t < 0.5
                ? Math.pow(2, 20 * t - 10) / 2
                : (2 - Math.pow(2, -20 * t + 10)) / 2;
        default:
          return t;
      }
    }

    function computeStarScale(canvasWidth: number, canvasHeight: number): number {
      const scaleX = 3.5 * (canvasWidth / starPath.width);
      const scaleY = 4 * (canvasHeight / starPath.height);

      return Math.max(scaleX, scaleY);
    }

    function drawFrame(ms: number) {
      if (!ctx) return; // Skip drawing if ctx is null

      const t = ms / 1000;
      const dpr = window.devicePixelRatio || 1;

      ctx.clearRect(0, 0, canvas.width / dpr, canvas.height / dpr);
      ctx.save();

      // Center the entire logo
      ctx.translate(canvas.width / dpr / 2 - 178 - 20, canvas.height / dpr / 2 - 61);

      const starProgress = ease(
        'easeInOutExpo',
        Math.min(Math.max((t - starAnimation.delay) / starAnimation.duration, 0), 1)
      );
      const initialStarScale = computeStarScale(canvas.width / dpr, canvas.height / dpr);
      starAnimation.scale = initialStarScale - (initialStarScale - 1) * starProgress;

      letterPaths.forEach(({ path, fill, x, y }, i) => {
        const anim = animations[i];

        // Staggered animations for each path
        const progress = ease('easeOutExpo', Math.min(Math.max((t - anim.delay) / anim.duration, 0), 1));

        anim.scale = 1.2 - 0.2 * progress;
        anim.opacity = progress;

        ctx.save();

        ctx.translate(x, y); // logo midpoint
        ctx.scale(anim.scale, anim.scale);
        ctx.rotate(anim.rotation);
        ctx.globalAlpha = anim.opacity;
        ctx.translate(-1 * x, -1 * y);

        ctx.fillStyle = fill;
        ctx.fill(path);

        ctx.restore();
      });

      // Final star at the minimum scale, purple color
      ctx.save();
      ctx.translate(27, 30);
      ctx.scale(starAnimation.scale, starAnimation.scale);
      ctx.globalAlpha = starProgress;
      ctx.translate(-27, -30);
      ctx.fillStyle = colors.dynamicPurple;
      ctx.fill(starPath.path);
      ctx.restore();

      // Starting star, deep blue fill
      ctx.save();
      ctx.translate(27, 30);
      ctx.scale(starAnimation.scale, starAnimation.scale);
      ctx.globalAlpha = 1 - starProgress;
      ctx.translate(-27, -30);
      ctx.fillStyle = colors.deepBlue;
      ctx.fill(starPath.path);
      ctx.restore();

      // Clipping path star, used to clip the blurred circles
      ctx.save();
      ctx.beginPath();
      ctx.translate(27, 30);
      ctx.scale(starAnimation.scale, starAnimation.scale);
      ctx.globalAlpha = 1 - starProgress;
      ctx.translate(-27, -30);
      ctx.clip(starPath.path);

      const minBlur = 100;
      const maxBlur = 300;
      const minScale = 1;
      const maxScale = starAnimation.scale;

      const normalized = maxScale - minScale;
      const blurAmount = minBlur + (maxBlur - minBlur) * normalized;

      ctx.filter = `blur(${blurAmount}px)`;
      ctx.globalAlpha = 1;

      const circles: Circle[] = [
        { x: 50, y: 34, rX: 36, rY: 18, color: colors.darkPurple },
        { x: 50, y: 38, rX: 32, rY: 16, color: colors.purple },
        { x: 50, y: 42, rX: 30, rY: 14, color: colors.magenta },
        { x: 50, y: 43, rX: 24, rY: 12, color: colors.red },
        { x: 50, y: 44, rX: 20, rY: 10, color: colors.darkGold },
      ];

      for (const { x, y, rX, rY, color } of circles) {
        ctx.beginPath();
        ctx.fillStyle = color;
        ctx.globalAlpha = 1 - starProgress;
        ctx.ellipse(x, y, rX, rY, 0, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.filter = 'none';
      ctx.restore();

      ctx.restore();

      // --- PAUSE BEFORE FADE ---
      const fadeStart = ANIMATION_DURATION - 0.5;
      const fadeDuration = 0.4;
      const fadeT = Math.min(Math.max((t - fadeStart) / fadeDuration, 0), 1);

      // If we've reached fadeStart and not yet paused, pause here
      if (!pausedAtFade && t >= fadeStart) {
        pausedAtFade = true;
        pausedTime = performance.now() - animationStart;
        return; // Stop drawing until resumed
      }
    }
    let animationStart = performance.now();
    function animate(timestamp: number) {
      if (pausedAtFade) return;
      const elapsed = timestamp - animationStart;
      const t = elapsed % (ANIMATION_DURATION * 1000);

      //scrubber.value = t; // Update scrubber slider position automatically
      drawFrame(t);
      window._animationReqId = requestAnimationFrame(animate);
    }
    return {
      animate: animate,
    };
  })();
  window.dpLoadLogo = dpLoadLogo;
  if (dpLoadLogo && typeof dpLoadLogo.animate === 'function') {
    requestAnimationFrame(dpLoadLogo.animate);
  }
});
setTimeout(() => {
  var dpLoaderWrapper = document.getElementById('single-preboot-new');
  if (dpLoaderWrapper) {
    simpleDebugLog.log('Removing preboot loader after a long timeout');
    dpLoaderWrapper.remove();
  }
}, 15000);

const handleLoad = () => {
  let hideLoaderTimeout = 0;

  // Show the loader for a minimum amount of time in case we loaded the assets
  // really quickly. window._prebootStartTime is defined in single_preboot.html
  if (window._prebootStartTime) {
    // Max time until we hide the loader no matter what.
    const maxTimeUntilLoaderHide = 4000;
    // Max time until we start animating out.
    const maxTimeUntilAnimateOut = maxTimeUntilLoaderHide - hideLoaderTimeout;
    const timeSinceLoaderShown = Date.now() - window._prebootStartTime;
    simpleDebugLog.log('Time since preboot loader shown', timeSinceLoaderShown, 'ms');

    // Sanity check in case the number is somehow really high so that we don't
    // show the loader forever.
    const timeUntilAnimateOut = Math.min(maxTimeUntilLoaderHide - timeSinceLoaderShown, maxTimeUntilAnimateOut);

    // Only adjust the timeouts if the number is positive as otherwise we've
    // already shown the loader for long enough.
    if (timeUntilAnimateOut > 0) {
      hideLoaderTimeout += timeUntilAnimateOut;
      simpleDebugLog.log('Showing preboot loader for another', hideLoaderTimeout, 'ms');
    }
  }

  // Hide the loader screen. We can remove it from DOM entirely since this will
  // never be shown after the app has loaded.
  setTimeout(() => {
    const dpLoadWrapper = document.getElementById('single-preboot-new-dp-loader');
    if (dpLoadWrapper) {
      simpleDebugLog.log('Hide preboot loader');
      const canvas = document.getElementById('logoCanvas');
      if (canvas) {
        canvas.classList.add('fade-out');
        // Remove loader after transition ends
        canvas.addEventListener('animationend', function handleFadeOut() {
          if (window._animationReqId !== null) {
            cancelAnimationFrame(window._animationReqId);
          }
          dpLoadWrapper.remove();
          canvas.removeEventListener('animationend', handleFadeOut);
        });
      } else {
        // fallback: remove immediately
        if (window._animationReqId !== null) {
          cancelAnimationFrame(window._animationReqId);
        }
        dpLoadWrapper.remove();
      }
    }
  }, hideLoaderTimeout);
};

document.readyState === 'complete' ? handleLoad() : window.addEventListener('load', handleLoad);
</script>

<style>
.single-preboot__wrapper {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100vh;
}
.single-preboot__topnav {
  flex: 0 0 42px;
  background-color: #f9f9f9;
}
.single-preboot__content {
  flex: 1 1 100%;
  min-height: 0;
  display: flex;
  align-items: stretch;
}
.single-preboot__leftbar {
  flex: 0 0 280px;
  background-color: #f9f9f9;
}
@media (prefers-color-scheme: dark) {
  .single-preboot__wrapper {
    background-color: #1f1f1f;
  }
  .single-preboot__topnav,
  .single-preboot__leftbar {
    background-color: #262626;
  }
  .single-preboot__body .preboot-preloader:before {
    border-color: #e9e9e9;
  }
}
@keyframes single-preboot-new-blob {
  from {
    transform: translate(0, 0) scale(1);
    rotate: 0deg;
  }
  20% {
    rotate: 25deg;
  }
  50% {
    transform: translate(45%, 25%) scale(1.5);
  }
  75% {
    rotate: 100deg;
  }
  to {
    rotate: 180deg;
  }
}
#single-preboot-new {
  position: fixed;
  top: 0;
  left: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100vw;
  height: 100vh;
  z-index: 100;
  background: var(--dt-color-surface-primary);
  --logo-size: 48px;
  --speed: 3600ms;
  --glyph-speed: 4700ms;
  --easing: cubic-bezier(0.68, -0.6, 0.32, 1.6);
  --total-letters: 7;
  --half-animation-duration: calc(var(--speed) / 2);
  --quarter-animation-duration: calc(var(--speed) / 4);
  --logo-animation-duration: calc(var(--speed) / 10);
  --dt-color-brand-purple: #7c52ff;
  --dt-color-brand-magenta: #f9008e;
  --dt-color-brand-gold: #f6ab3c;
  --dt-color-foreground-primary: #000;
}
@media (prefers-color-scheme: dark) {
  #single-preboot-new {
    --dt-color-brand-purple: #7c52ff;
    --dt-color-brand-magenta: #f9008e;
    --dt-color-brand-gold: #f6ab3c;
    --dt-color-foreground-primary: #e6e6e6;
  }
}
#single-preboot-new .dp-loader {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}
#logoCanvas {
  --animation-duration: 3;
}

@media (min-width: 640px) {
  #logoCanvas {
    --animation-duration: 4;
  }
}

@media (min-width: 1264px) {
  #logoCanvas {
    --animation-duration: 6;
  }
}
#single-preboot-new-dp-loader {
  height: inherit;
}

#single-preboot-new-dp-loader canvas.fade-out {
  transition: opacity 0.4s ease;
  opacity: 0;
}
</style>
